import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

// import { chartTimePeriod } from '../../../config/enum';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import stationManagementApi from '../../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChartWidthInterface {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Custom square bar component
 * @param {any} properties - The properties
 * @returns {any} The result
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
        />
      ))}
    </>
  );
}
/**
 * Uptime BY Station Chart
 * @returns {any} The rendered component
 */
function UptimeStationChart() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { selectedStationPerformanceList } = useAppSelector((state) => state.stationManagement);
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [chartWidth, setChartWidth] = useState<ChartWidthInterface>({
    type: 'percentage',
    width: 100,
  });
  /**
   * Function to show custom tooltip
   * @param {any} properties - The tooltip props
   * @returns {React.ReactNode} The rendered tooltip
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <div className="tooltip-values">
            <p className="uptime-tooltip-station">
              {t('stationName', 'Station Name')}: {data.stationName}
            </p>
            <p className="uptime-tooltip-hours">
              {t('totalOnlineHours', 'Total Online Hours')}: {data.rawHours}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param {any} range The time range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setLoading(true);
    try {
      const response = await stationManagementApi.fetchUptimeChartData({
        timePeriod: range,
        chargingStation: selectedStationPerformanceList?.id,
      });
      if (response.status === 200) {
        const values = response.data.map((item: any) => item.totalOnlineHours);
        const maxValue = Math.max(...values) || 1;

        const formatted = response.data
          .map((item: any) => ({
            stationName: item.stationName,
            rawHours: item.totalOnlineHours,
            totalOnlineHours: (item.totalOnlineHours / maxValue) * 100,
          }))
          // 🔽 DESCENDING ORDER (greater → smaller)
          .sort((a: any, b: any) => b.rawHours - a.rawHours);

        setBarGraphData(formatted);
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  useEffect(() => {
    if (selectedStationPerformanceList?.id) {
      fetchPowerConsumedReport('last24hours');
    }
  }, [selectedStationPerformanceList]);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);

  return (
    <div className="uptime-by-station-chart-wrap" dir={i18n.dir()}>
      <div className="uptime-by-station-container">
        <div className="uptime-by-header">
          <div className="uptime-title">
            <div className="uptime-title-main">
              <span className="title-text">
                {t('stationUptimePercentage', 'Station Uptime Percentage (%)')}
              </span>
              <InfoIcon className="info-icon" />
            </div>
            <p className="subtitle-text">
              {t(
                'dailyUptimePercentageTrendDesc',
                'Daily uptime percentage trend of all stations.',
              )}
            </p>
          </div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({ startDate: start, endDate: end });
              // Trigger fetch with default range; you can adapt to use dates later
              fetchPowerConsumedReport('last24hours');
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          />
        </div>
        <div className="uptime-by-station-chart-container">
          {loading ? (
            <div className="loader-button">
              <BatteryLoader />
            </div>
          ) : (
            <div className="uptime-by-station-chart">
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={209}>
                <BarChart
                  data={barGraphData}
                  margin={{
                    left: i18n.dir() === 'rtl' ? 20 : 0,
                    right: i18n.dir() === 'rtl' ? 0 : 20,
                  }}>
                  <XAxis
                    reversed={i18n.dir() === 'rtl'}
                    dataKey="stationName"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 500 }}
                    interval={0}
                    height={20}
                    tickFormatter={(value) => {
                      const translatedValue = value ? t(value) : value;
                      return translatedValue?.length > 3
                        ? `${translatedValue.slice(0, 3)}...`
                        : translatedValue;
                    }}
                  />
                  <YAxis
                    orientation={i18n.dir() === 'rtl' ? 'right' : 'left'}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 600 }}
                    width={i18n.dir() === 'rtl' ? 65 : 50}
                    tickMargin={i18n.dir() === 'rtl' ? 25 : 0}
                    padding={{ top: 20, bottom: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="totalOnlineHours"
                    fill="#4ABDFB"
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UptimeStationChart;
